import { memo, useState, useRef } from 'react';
import {
  Button,
  Flex,
  Text,
  Popover,
  PopoverTrigger,
  Portal,
  PopoverContent,
  Box,
  Tooltip,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Icon,
  useOutsideClick,
} from '@chakra-ui/react';
import { MdHelp } from 'react-icons/md';
import { useSelector } from 'react-redux';

import ContactSupportIcon from 'components/Icons/ContactSupportIcon';
import ShareButtonWithPopup from 'components/Header/ShareButtonWithPopup';
import ShareIcon from 'components/Icons/ShareIcon';
// import LightFilledUserAvatar from 'components/Icons/FilledLightUserAvatar';
import LogoutModal from 'components/LogoutModal';
import { getUserData } from 'redux/UserSlice';
import HelpV1Icon from 'components/Icons/HelpV1Icon';
import UserIconV1 from 'components/UserIconV1';

import UserMenuPanel from './UserMenuPanel';

const UserActions = memo(({ globalHeader }: { globalHeader?: boolean }) => {
  const userData = useSelector(getUserData);
  const [logoutModal, setLogoutModal] = useState(false);
  const shareButtonReference = useRef<HTMLDivElement>(null);
  const [isUserPopoverOpen, setIsUserPopoverOpen] = useState(false);
  const userPopoverReference = useRef<HTMLDivElement | null>(null);

  useOutsideClick({
    ref: userPopoverReference,
    handler: () => {
      if (isUserPopoverOpen) {
        setIsUserPopoverOpen(false);
      }
    },
  });

  const handleMenuItemClick = (url: string) => {
    window.open(url, '_blank');
  };

  return (
    <>
      <Flex alignItems='center' gap={'12px'} height={'32px'} flexShrink={0} width='auto'>
        {!globalHeader && (
          <>
            <Menu>
              {({ isOpen }) => {
                return (
                  <>
                    <MenuButton
                      as={Button}
                      variant='unstyled'
                      p={0}
                      minW='0px'
                      width='33px'
                      height='33px'
                      aria-label='Help'
                    >
                      <div
                        style={{
                          width: '33px',
                          height: '33px',
                          padding: 8,
                          background: 'white',
                          borderRadius: 50,
                          outline: '1px #D9D9D9 solid',
                          outlineOffset: '-1px',
                          justifyContent: 'center',
                          alignItems: 'center',
                          gap: 8,
                          display: 'inline-flex',
                        }}
                      >
                        <HelpV1Icon />
                      </div>
                    </MenuButton>
                    {isOpen && (
                      <MenuList
                        zIndex={9}
                        minW='200px'
                        py={0}
                        borderRadius='xl'
                        boxShadow='0px 4px 12px rgba(0, 0, 0, 0.08)'
                        border='1px solid'
                        borderColor='gray.200'
                      >
                        <MenuItem
                          py={3}
                          px={4}
                          onClick={() => handleMenuItemClick('https://help.blox.so/')}
                          _hover={{ bg: 'gray.50' }}
                          borderBottom='1px solid'
                          borderColor='gray.100'
                        >
                          <Flex alignItems='center' gap={3}>
                            <Box
                              boxSize={8}
                              borderRadius='full'
                              bg='#F1EEFF'
                              display='flex'
                              alignItems='center'
                              justifyContent='center'
                            >
                              <Icon as={MdHelp} color='#000000' boxSize={4} />
                            </Box>
                            <Text fontWeight='500' color='black'>
                              Help Center
                            </Text>
                          </Flex>
                        </MenuItem>
                        {/* <MenuItem
                      py={3}
                      px={4}
                      onClick={() => {}}
                      _hover={{ bg: 'gray.50' }}
                      borderBottom='1px solid'
                      borderColor='gray.100'
                    >
                      <Flex alignItems='center' gap={3}>
                        <Box
                          boxSize={8}
                          borderRadius='full'
                          bg='#F1EEFF'
                          display='flex'
                          alignItems='center'
                          justifyContent='center'
                        >
                          <Icon as={MdDescription} color='#745BE3' boxSize={4} />
                        </Box>
                        <Text fontWeight='500' color='black'>
                          What's New
                        </Text>
                      </Flex>
                    </MenuItem> */}
                        <MenuItem
                          py={3}
                          px={4}
                          onClick={() => handleMenuItemClick('https://www.blox.so/contact')}
                          _hover={{ bg: 'gray.50' }}
                        >
                          <Flex alignItems='center' gap={3}>
                            <Box
                              boxSize={8}
                              borderRadius='full'
                              bg='#F1EEFF'
                              display='flex'
                              alignItems='center'
                              justifyContent='center'
                            >
                              <ContactSupportIcon width={16} height={16} color='#745BE3' />
                            </Box>
                            <Text fontWeight='500' color='black'>
                              Contact Support
                            </Text>
                          </Flex>
                        </MenuItem>
                      </MenuList>
                    )}
                  </>
                );
              }}
            </Menu>

            <Tooltip label='Share model' hasArrow placement='bottom'>
              <Button
                variant='solid'
                bg='white'
                borderRadius='10px'
                border='1px solid #D9D9D9'
                height='32px'
                minW='40px'
                width='fit-content'
                p={0}
                _hover={{ bg: 'gray.50' }}
                aria-label='Share modal'
                onClick={() => {
                  // Trigger ShareButtonWithPopup's button click
                  const shareButton = shareButtonReference.current?.querySelector('button');
                  if (shareButton) {
                    shareButton.click();
                  }
                }}
              >
                <Flex alignItems='center' justifyContent='center' gap={2} px={3}>
                  <ShareIcon />
                  <Text fontSize='12px' fontWeight='500' color='#121826'>
                    Share model
                  </Text>
                </Flex>
              </Button>
            </Tooltip>

            <Box
              ref={shareButtonReference}
              position='absolute'
              visibility='hidden'
              pointerEvents='none'
            >
              <ShareButtonWithPopup />
            </Box>
          </>
        )}
        <Popover
          isLazy
          gutter={16}
          placement='bottom-end'
          isOpen={isUserPopoverOpen}
          onClose={() => setIsUserPopoverOpen(false)}
        >
          <Box ref={userPopoverReference}>
            <PopoverTrigger>
              <Button
                variant='solid'
                bg='white'
                borderRadius='12px'
                width='fit-content'
                height='33px'
                border='1px solid #D9D9D9'
                px={2}
                _hover={{ bg: 'gray.50' }}
                aria-label='Open user menu'
                onClick={() => setIsUserPopoverOpen((previous) => !previous)}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <UserIconV1 />
                  <Text fontSize='12px' fontWeight='500' color='black'>
                    {`${userData?.firstname} ${userData?.lastname}`}
                  </Text>
                </div>
              </Button>
            </PopoverTrigger>
            <Portal>
              <PopoverContent
                position='absolute'
                right='0'
                p={0}
                w='fit-content'
                minWidth='280px'
                color='black'
                borderWidth='1px'
                borderRadius='8px'
                borderColor='gray.200'
                bg='white'
                boxShadow='0px 4px 12px rgba(0, 0, 0, 0.08)'
                mr='2'
              >
                <UserMenuPanel
                  onRequestClose={() => setIsUserPopoverOpen(false)}
                  onLogoutRequest={() => setLogoutModal(true)}
                />
              </PopoverContent>
            </Portal>
          </Box>
        </Popover>
      </Flex>

      {logoutModal && (
        <LogoutModal
          isOpen={logoutModal}
          onClose={() => setLogoutModal(false)}
          onOpenAuthModal={() => {}}
        />
      )}
    </>
  );
});

UserActions.displayName = 'UserActions';

export default UserActions;
